// Global Instructions Rule Applied!
// Shape of GET /capital-calls/:id/notices (view__capital_call_notices): the
// Notices section of the capital call form - per LP the stored notice the
// list shows, what stops the notices being produced, and the counts.
import { z } from 'zod'
import { validateWithZodSchema } from '../../core/utils/utilities.ts'

/** cfg.capital_call_notice_statuses.code - what the notice RPCs match on. */
export const NoticeStatusCode = {
  pending: 'pending',
  generated: 'generated',
  failed: 'failed',
  superseded: 'superseded'
} as const

/** prog__send_state - where a stored notice stands in a notice email. */
export const NoticeSendState = {
  sent: 'sent',
  draft: 'draft',
  queued: 'queued',
  sending: 'sending',
  failed: 'failed'
} as const

/** An additional document stored with a notice (the ILPA workbook). */
export const NoticeSupplementFileSchema = z
  .object({
    file_urls_capital_call_notices__id: z.string(),
    file_urls_capital_call_notices__file_name: z.string(),
    file_urls_capital_call_notices__file_url: z.string(),
    file_urls_capital_call_notices__file_size_bytes: z.number().nullish(),
    notice_templates__code: z.string(),
    notice_templates__label: z.string().nullish(),
    // what the document goes by in a tag ("ILPA")
    notice_templates__short_label: z.string().nullish()
  })
  .passthrough()

export const CapitalCallNoticeRowSchema = z
  .object({
    fund_commitments__id: z.string(),
    investors__id: z.string().nullish(),
    investors__name: z.string().nullish(),
    investors__legal_name: z.string().nullish(),
    accounts__id: z.string().nullish(),
    accounts__name: z.string().nullish(),
    // false for an LP that holds a stored notice but is no longer on the call
    prog__on_call: z.boolean(),
    prog__call_total: z.number().nullish(),
    prog__is_initial: z.boolean().nullish(),
    // the notice shown: the LP's current one, else its last superseded one
    capital_call_notices__id: z.string().nullish(),
    // above 1: the call was reverted and issued again ("Revised")
    capital_call_notices__version: z.number().nullish(),
    capital_call_notices__is_current: z.boolean(),
    capital_call_notices__generated_at: z.string().nullish(),
    capital_call_notices__page_count: z.number().nullish(),
    capital_call_notices__error_message: z.string().nullish(),
    capital_call_notices__date_created: z.string().nullish(),
    capital_call_notice_statuses__code: z.string().nullish(),
    capital_call_notice_statuses__label: z.string().nullish(),
    capital_call_notice_statuses__tag_color: z.string().nullish(),
    notice_templates__label: z.string().nullish(),
    file_urls_capital_call_notices__id: z.string().nullish(),
    file_urls_capital_call_notices__file_name: z.string().nullish(),
    file_urls_capital_call_notices__file_url: z.string().nullish(),
    file_urls_capital_call_notices__file_size_bytes: z.number().nullish(),
    // the additional documents stored with the notice shown; defaulted for
    // older backends
    supplement_files: z.array(NoticeSupplementFileSchema).optional().default([]),
    // the stored notice no longer matches the live data it would be built from
    prog__is_stale: z.boolean(),
    // Pending for longer than the configured limit
    prog__is_stalled: z.boolean(),
    prog__superseded_count: z.number(),
    // when the notice shown first reached the LP; null while it has not
    prog__sent_at: z.string().nullish(),
    // sent / draft / queued / sending / failed; null when no email carries it
    prog__send_state: z.string().nullish(),
    // the email that sent it, else the newest one carrying it
    email_campaigns__id: z.string().nullish(),
    email_campaigns__name: z.string().nullish()
  })
  .passthrough()

/** One notice email of the call. */
export const CapitalCallNoticeCampaignSchema = z
  .object({
    email_campaigns__id: z.string(),
    email_campaigns__name: z.string(),
    email_campaigns__status: z.string(),
    email_campaigns__sent_count: z.number(),
    email_campaigns__recipient_count: z.number(),
    email_campaigns__completed_at: z.string().nullish()
  })
  .passthrough()

export const CapitalCallNoticesSchema = z
  .object({
    call: z
      .object({
        capital_calls__id: z.string(),
        capital_calls__name: z.string().nullish(),
        capital_calls__notice_purpose: z.string().nullish(),
        status_label: z.string().nullish(),
        // true once the call is issued: only then are notices stored
        counts_as_called: z.boolean(),
        // false for an intercompany pass-through: LPs get no notice for it
        prog__notice_applicable: z.boolean(),
        prog__is_consolidated: z.boolean()
      })
      .passthrough(),
    fund: z
      .object({
        funds__id: z.string(),
        funds__name: z.string().nullish(),
        funds__legal_name: z.string().nullish()
      })
      .passthrough(),
    // the layout the next generation renders with (= the document report code)
    settings: z.object({ template_code: z.string().nullish(), template_label: z.string().nullish() }).passthrough(),
    // sentences for the user: what stops a notice being produced
    prog__problems: z.array(z.string()),
    data: z.array(CapitalCallNoticeRowSchema),
    // the notice emails of the call, newest first
    campaigns: z.array(CapitalCallNoticeCampaignSchema),
    // why "Send notices" is not available, as a sentence; null when it is
    prog__send_blocked_reason: z.string().nullish(),
    prog__summary: z
      .object({
        total: z.number(),
        generated: z.number(),
        pending: z.number(),
        stalled: z.number(),
        failed: z.number(),
        stale: z.number(),
        not_generated: z.number(),
        // LPs whose current notice was sent
        sent: z.number()
      })
      .passthrough()
  })
  .passthrough()

export type CapitalCallNoticeRow = z.infer<typeof CapitalCallNoticeRowSchema>
export type CapitalCallNoticeCampaign = z.infer<typeof CapitalCallNoticeCampaignSchema>
export type CapitalCallNotices = z.infer<typeof CapitalCallNoticesSchema>

export function validateCapitalCallNotices(data: unknown): CapitalCallNotices {
  try {
    return validateWithZodSchema(CapitalCallNoticesSchema, data, 'capital call notices')
  } catch (error) {
    throw new Error(`Capital call notices validation failed: ${error}`)
  }
}
